/**
 * Service profiles (Leistungsbilder) of the HOAI 2021 with a fee table
 * (official text, § 5 UrhG): Teil 2 Flächenplanung, Teil 3 Objektplanung,
 * Teil 4 Fachplanung. Written by ops/hoai/leistungsbilder.py on
 * 2026-10-10 from gesetze-im-internet.de - do not edit by hand.
 * Orientation values, not binding rates.
 *
 * Phases, criteria, points, zone bounds and conversion supplements are read
 * from the text and checked; costRules are short summaries checked against
 * the wording (see the script).
 */
import { HOAI_TABLE_BPLAN, HOAI_TABLE_FNP, HOAI_TABLE_FREIANLAGEN, HOAI_TABLE_GEBAEUDE, HOAI_TABLE_GRUENORDNUNGSPLAN, HOAI_TABLE_INGENIEURBAUWERKE, HOAI_TABLE_LANDSCHAFTSPLAN, HOAI_TABLE_LANDSCHAFTSRAHMENPLAN, HOAI_TABLE_LBP, HOAI_TABLE_PEP, HOAI_TABLE_TGA, HOAI_TABLE_TRAGWERK, HOAI_TABLE_VERKEHRSANLAGEN } from './hoai-tables';
export const HOAI_PARTS = ['Flächenplanung', 'Objektplanung', 'Fachplanung'];
/** Order: Teil 2, Teil 3, Teil 4. */
export const HOAI_SERVICE_KEYS = [
    // Flächenplanung
    'fnp', 'bplan', 'landschaftsplan', 'gruenordnungsplan', 'landschaftsrahmenplan', 'lbp', 'pep',
    // Objektplanung
    'gebaeude', 'innenraeume', 'freianlagen', 'ingenieurbauwerke', 'verkehrsanlagen',
    // Fachplanung
    'tragwerk', 'tga',
];
/** § 12 Abs. 2: highest increase of LP 8 for repairs, percent of its share. */
export const HOAI_MAINTENANCE_LP8_MAX = 50;
export const HOAI_SERVICE_PROFILES = {
    fnp: {
        key: 'fnp',
        name: 'Flächennutzungsplan',
        part: 'Flächenplanung',
        paragraphs: { services: '§ 18', fees: '§ 20', basics: '§ 20' },
        url: 'https://www.gesetze-im-internet.de/hoai_2013/__20.html',
        table: HOAI_TABLE_FNP,
        basis: 'hectare',
        basisLabel: 'Fläche in Hektar',
        zones: 3,
        zoneLabels: ['geringe Anforderungen', 'durchschnittliche Anforderungen', 'hohe Anforderungen'],
        phases: [
            { name: 'Vorentwurf für die frühzeitigen Beteiligungen', share: 60 },
            { name: 'Entwurf zur öffentlichen Auslegung', share: 30 },
            { name: 'Plan zur Beschlussfassung', share: 10 },
        ],
        criteria: [
            { label: 'zentralörtliche Bedeutung und Gemeindestruktur', max: 3 },
            { label: 'Nutzungsvielfalt und Nutzungsdichte', max: 3 },
            { label: 'Einwohnerstruktur, Einwohnerentwicklung und Gemeinbedarfsstandorte', max: 3 },
            { label: 'Verkehr und Infrastruktur', max: 3 },
            { label: 'Topografie, Geologie und Kulturlandschaft', max: 3 },
            { label: 'Klima-, Natur- und Umweltschutz', max: 3 },
        ],
        criteriaRef: null,
        bounds: [9, 14, 18],
        maxConversion: null,
        conversionDefault: null,
        conversionSource: null,
        costRules: 'Das Honorar ist nach der Fläche des Plangebiets in Hektar und nach der Honorarzone zu berechnen (§ 20 Abs. 2). Werden Teilflächen bereits aufgestellter Flächennutzungspläne (Planausschnitte) geändert oder überarbeitet, kann das Honorar auch abweichend davon vereinbart werden (§ 20 Abs. 6).',
        maintenanceLp8: false,
    },
    bplan: {
        key: 'bplan',
        name: 'Bebauungsplan',
        part: 'Flächenplanung',
        paragraphs: { services: '§ 19', fees: '§ 21', basics: '§ 21' },
        url: 'https://www.gesetze-im-internet.de/hoai_2013/__21.html',
        table: HOAI_TABLE_BPLAN,
        basis: 'hectare',
        basisLabel: 'Fläche in Hektar',
        zones: 3,
        zoneLabels: ['geringe Anforderungen', 'durchschnittliche Anforderungen', 'hohe Anforderungen'],
        phases: [
            { name: 'Vorentwurf für die frühzeitigen Beteiligungen', share: 60 },
            { name: 'Entwurf zur öffentlichen Auslegung', share: 30 },
            { name: 'Plan zur Beschlussfassung', share: 10 },
        ],
        criteria: [
            { label: 'Nutzungsvielfalt und Nutzungsdichte', max: 3 },
            { label: 'Baustruktur und Baudichte', max: 3 },
            { label: 'Gestaltung und Denkmalschutz', max: 3 },
            { label: 'Verkehr und Infrastruktur', max: 3 },
            { label: 'Topografie und Landschaft', max: 3 },
            { label: 'Klima-,Natur- und Umweltschutz', max: 3 },
        ],
        criteriaRef: null,
        bounds: [9, 14, 18],
        maxConversion: null,
        conversionDefault: null,
        conversionSource: null,
        costRules: 'Das Honorar ist nach der Fläche des Plangebiets in Hektar und nach der Honorarzone zu berechnen (§ 21 Abs. 2). Wird die Größe des Plangebiets im förmlichen Verfahren während der Leistungserbringung geändert, ist das Honorar für die noch nicht erbrachten Leistungsphasen nach der geänderten Größe zu berechnen (§ 21 Abs. 5).',
        maintenanceLp8: false,
    },
    landschaftsplan: {
        key: 'landschaftsplan',
        name: 'Landschaftsplan',
        part: 'Flächenplanung',
        paragraphs: { services: '§ 23', fees: '§ 28', basics: '§ 28' },
        url: 'https://www.gesetze-im-internet.de/hoai_2013/__28.html',
        table: HOAI_TABLE_LANDSCHAFTSPLAN,
        basis: 'hectare',
        basisLabel: 'Fläche in Hektar',
        zones: 3,
        zoneLabels: ['geringe Anforderungen', 'durchschnittliche Anforderungen', 'hohe Anforderungen'],
        phases: [
            { name: 'Klären der Aufgabenstellung und Ermitteln des Leistungsumfangs', share: 3 },
            { name: 'Ermitteln der Planungsgrundlagen', share: 37 },
            { name: 'Vorläufige Fassung', share: 50 },
            { name: 'Abgestimmte Fassung', share: 10 },
        ],
        criteria: [
            { label: 'topographische Verhältnisse', max: 6 },
            { label: 'Flächennutzung', max: 6 },
            { label: 'Landschaftsbild', max: 6 },
            { label: 'Anforderungen an Umweltsicherung und Umweltschutz', max: 9 },
            { label: 'ökologische Verhältnisse', max: 9 },
            { label: 'Bevölkerungsdichte', max: 6 },
        ],
        criteriaRef: null,
        bounds: [16, 30, 42],
        maxConversion: null,
        conversionDefault: null,
        conversionSource: null,
        costRules: 'Das Honorar ist nach der Fläche des Planungsgebiets in Hektar und nach der Honorarzone zu berechnen (§ 28 Abs. 2). Werden Teilflächen bereits aufgestellter Landschaftspläne (Planausschnitte) geändert oder überarbeitet, kann das Honorar abweichend davon vereinbart werden (§ 28 Abs. 6).',
        maintenanceLp8: false,
    },
    gruenordnungsplan: {
        key: 'gruenordnungsplan',
        name: 'Grünordnungsplan',
        part: 'Flächenplanung',
        paragraphs: { services: '§ 24', fees: '§ 29', basics: '§ 29' },
        url: 'https://www.gesetze-im-internet.de/hoai_2013/__29.html',
        table: HOAI_TABLE_GRUENORDNUNGSPLAN,
        basis: 'hectare',
        basisLabel: 'Fläche in Hektar',
        zones: 3,
        zoneLabels: ['geringe Anforderungen', 'durchschnittliche Anforderungen', 'hohe Anforderungen'],
        phases: [
            { name: 'Klären der Aufgabenstellung und Ermitteln des Leistungsumfangs', share: 3 },
            { name: 'Ermitteln der Planungsgrundlagen', share: 37 },
            { name: 'Vorläufige Fassung', share: 50 },
            { name: 'Abgestimmte Fassung', share: 10 },
        ],
        criteria: [
            { label: 'Topographie', max: 6 },
            { label: 'ökologische Verhältnisse', max: 6 },
            { label: 'Flächennutzungen und Schutzgebiete', max: 6 },
            { label: 'Umwelt-, Klima-, Denkmal- und Naturschutz', max: 9 },
            { label: 'Erholungsvorsorge', max: 6 },
            { label: 'Anforderung an die Freiraumgestaltung', max: 9 },
        ],
        criteriaRef: null,
        bounds: [16, 30, 42],
        maxConversion: null,
        conversionDefault: null,
        conversionSource: null,
        costRules: 'Das Honorar ist nach der Fläche des Planungsgebiets in Hektar und nach der Honorarzone zu berechnen (§ 29 Abs. 2). Wird die Größe des Planungsgebiets während der Leistungserbringung geändert, ist das Honorar für die noch nicht erbrachten Leistungsphasen nach der geänderten Größe zu berechnen (§ 29 Abs. 6).',
        maintenanceLp8: false,
    },
    landschaftsrahmenplan: {
        key: 'landschaftsrahmenplan',
        name: 'Landschaftsrahmenplan',
        part: 'Flächenplanung',
        paragraphs: { services: '§ 25', fees: '§ 30', basics: '§ 30' },
        url: 'https://www.gesetze-im-internet.de/hoai_2013/__30.html',
        table: HOAI_TABLE_LANDSCHAFTSRAHMENPLAN,
        basis: 'hectare',
        basisLabel: 'Fläche in Hektar',
        zones: 3,
        zoneLabels: ['geringe Anforderungen', 'durchschnittliche Anforderungen', 'hohe Anforderungen'],
        phases: [
            { name: 'Klären der Aufgabenstellung und Ermitteln des Leistungsumfangs', share: 3 },
            { name: 'Ermitteln der Planungsgrundlagen', share: 37 },
            { name: 'Vorläufige Fassung', share: 50 },
            { name: 'Abgestimmte Fassung', share: 10 },
        ],
        criteria: [
            { label: 'topographische Verhältnisse', max: 6 },
            { label: 'Raumnutzung und Bevölkerungsdichte', max: 6 },
            { label: 'Landschaftsbild', max: 6 },
            { label: 'Anforderungen an Umweltsicherung, Klima- und Naturschutz', max: 9 },
            { label: 'ökologische Verhältnisse', max: 9 },
            { label: 'Freiraumsicherung und Erholung', max: 6 },
        ],
        criteriaRef: null,
        bounds: [16, 30, 42],
        maxConversion: null,
        conversionDefault: null,
        conversionSource: null,
        costRules: 'Das Honorar ist nach der Fläche des Planungsgebiets in Hektar und nach der Honorarzone zu berechnen (§ 30 Abs. 2). Wird die Größe des Planungsgebiets während der Leistungserbringung geändert, ist das Honorar für die noch nicht erbrachten Leistungsphasen nach der geänderten Größe zu berechnen (§ 30 Abs. 6).',
        maintenanceLp8: false,
    },
    lbp: {
        key: 'lbp',
        name: 'Landschaftspflegerischer Begleitplan',
        part: 'Flächenplanung',
        paragraphs: { services: '§ 26', fees: '§ 31', basics: '§ 31' },
        url: 'https://www.gesetze-im-internet.de/hoai_2013/__31.html',
        table: HOAI_TABLE_LBP,
        basis: 'hectare',
        basisLabel: 'Fläche in Hektar',
        zones: 3,
        zoneLabels: ['geringe Anforderungen', 'durchschnittliche Anforderungen', 'hohe Anforderungen'],
        phases: [
            { name: 'Klären der Aufgabenstellung und Ermitteln des Leistungsumfangs', share: 3 },
            { name: 'Ermitteln und Bewerten der Planungsgrundlagen', share: 37 },
            { name: 'Vorläufige Fassung', share: 50 },
            { name: 'Abgestimmte Fassung', share: 10 },
        ],
        criteria: [
            { label: 'ökologisch bedeutsame Strukturen und Schutzgebiete', max: 6 },
            { label: 'Landschaftsbild und Erholungsnutzung', max: 6 },
            { label: 'Nutzungsansprüche', max: 6 },
            { label: 'Anforderungen an die Gestaltung von Landschaft und Freiraum', max: 6 },
            { label: 'Empfindlichkeit gegenüber Umweltbelastungen und Beeinträchtigungen von Natur und Landschaft', max: 9 },
            { label: 'potenzielle Beeinträchtigungsintensität der Maßnahme', max: 9 },
        ],
        criteriaRef: null,
        bounds: [16, 30, 42],
        maxConversion: null,
        conversionDefault: null,
        conversionSource: null,
        costRules: 'Das Honorar ist nach der Fläche des Planungsgebiets in Hektar und nach der Honorarzone zu berechnen (§ 31 Abs. 2). Wird die Größe des Planungsgebiets während der Leistungserbringung geändert, ist das Honorar für die noch nicht erbrachten Leistungsphasen nach der geänderten Größe zu berechnen (§ 31 Abs. 6).',
        maintenanceLp8: false,
    },
    pep: {
        key: 'pep',
        name: 'Pflege- und Entwicklungsplan',
        part: 'Flächenplanung',
        paragraphs: { services: '§ 27', fees: '§ 32', basics: '§ 32' },
        url: 'https://www.gesetze-im-internet.de/hoai_2013/__32.html',
        table: HOAI_TABLE_PEP,
        basis: 'hectare',
        basisLabel: 'Fläche in Hektar',
        zones: 3,
        zoneLabels: ['geringe Anforderungen', 'durchschnittliche Anforderungen', 'hohe Anforderungen'],
        phases: [
            { name: 'Zusammenstellen der Ausgangsbedingungen', share: 3 },
            { name: 'Ermitteln der Planungsgrundlagen', share: 37 },
            { name: 'Vorläufige Fassung', share: 50 },
            { name: 'Abgestimmte Fassung', share: 10 },
        ],
        criteria: [
            { label: 'fachliche Vorgaben', max: 4 },
            { label: 'Differenziertheit des floristischen Inventars oder der Pflanzengesellschaften', max: 9 },
            { label: 'Differenziertheit des faunistischen Inventars', max: 9 },
            { label: 'Beeinträchtigungen oder Schädigungen von Naturhaushalt und Landschaftsbild', max: 6 },
            { label: 'Aufwand für die Festlegung von Zielaussagen sowie für Pflege- und Entwicklungsmaßnahmen', max: 6 },
        ],
        criteriaRef: null,
        bounds: [13, 24, 34],
        maxConversion: null,
        conversionDefault: null,
        conversionSource: null,
        costRules: 'Das Honorar ist nach der Fläche des Planungsgebiets in Hektar und nach der Honorarzone zu berechnen (§ 32 Abs. 2). Wird die Größe des Planungsgebiets während der Leistungserbringung geändert, ist das Honorar für die noch nicht erbrachten Leistungsphasen nach der geänderten Größe zu berechnen (§ 32 Abs. 6).',
        maintenanceLp8: false,
    },
    gebaeude: {
        key: 'gebaeude',
        name: 'Gebäude',
        part: 'Objektplanung',
        paragraphs: { services: '§ 34', fees: '§ 35', basics: '§ 33' },
        url: 'https://www.gesetze-im-internet.de/hoai_2013/__35.html',
        table: HOAI_TABLE_GEBAEUDE,
        basis: 'costs',
        basisLabel: 'Anrechenbare Kosten in Euro',
        zones: 5,
        zoneLabels: ['sehr geringe Anforderungen', 'geringe Anforderungen', 'durchschnittliche Anforderungen', 'hohe Anforderungen', 'sehr hohe Anforderungen'],
        phases: [
            { name: 'Grundlagenermittlung', share: 2 },
            { name: 'Vorplanung', share: 7 },
            { name: 'Entwurfsplanung', share: 15 },
            { name: 'Genehmigungsplanung', share: 3 },
            { name: 'Ausführungsplanung', share: 25 },
            { name: 'Vorbereitung der Vergabe', share: 10 },
            { name: 'Mitwirkung bei der Vergabe', share: 4 },
            { name: 'Objektüberwachung – Bauüberwachung und Dokumentation', share: 32 },
            { name: 'Objektbetreuung', share: 2 },
        ],
        criteria: [
            { label: 'Anforderungen an die Einbindung in die Umgebung', max: 6 },
            { label: 'Anzahl der Funktionsbereiche', max: 9 },
            { label: 'gestalterische Anforderungen', max: 9 },
            { label: 'konstruktive Anforderungen', max: 6 },
            { label: 'technische Ausrüstung', max: 6 },
            { label: 'Ausbau', max: 6 },
        ],
        criteriaRef: null,
        bounds: [10, 18, 26, 34, 42],
        maxConversion: 33,
        conversionDefault: 20,
        conversionSource: '§ 36 Abs. 1',
        costRules: 'Anrechenbar sind die Kosten der Baukonstruktion (§ 33 Abs. 1). Kosten für Technische Anlagen, die der Auftragnehmer nicht fachlich plant oder deren Ausführung er nicht fachlich überwacht, sind bis zu einem Betrag von 25 Prozent der sonstigen anrechenbaren Kosten vollständig und mit dem darüber hinausgehenden Betrag zur Hälfte anrechenbar (§ 33 Abs. 2). Nicht anrechenbar sind insbesondere die Kosten für das Herrichten, die nichtöffentliche Erschließung, Ausstattung und Kunstwerke, soweit der Auftragnehmer sie weder plant noch bei der Beschaffung mitwirkt oder ihre Ausführung oder ihren Einbau fachlich überwacht (§ 33 Abs. 3).',
        maintenanceLp8: true,
    },
    innenraeume: {
        key: 'innenraeume',
        name: 'Innenräume',
        part: 'Objektplanung',
        paragraphs: { services: '§ 34', fees: '§ 35', basics: '§ 33' },
        url: 'https://www.gesetze-im-internet.de/hoai_2013/__35.html',
        table: HOAI_TABLE_GEBAEUDE,
        basis: 'costs',
        basisLabel: 'Anrechenbare Kosten in Euro',
        zones: 5,
        zoneLabels: ['sehr geringe Anforderungen', 'geringe Anforderungen', 'durchschnittliche Anforderungen', 'hohe Anforderungen', 'sehr hohe Anforderungen'],
        phases: [
            { name: 'Grundlagenermittlung', share: 2 },
            { name: 'Vorplanung', share: 7 },
            { name: 'Entwurfsplanung', share: 15 },
            { name: 'Genehmigungsplanung', share: 2 },
            { name: 'Ausführungsplanung', share: 30 },
            { name: 'Vorbereitung der Vergabe', share: 7 },
            { name: 'Mitwirkung bei der Vergabe', share: 3 },
            { name: 'Objektüberwachung – Bauüberwachung und Dokumentation', share: 32 },
            { name: 'Objektbetreuung', share: 2 },
        ],
        criteria: [
            { label: 'Anzahl der Funktionsbereiche', max: 6 },
            { label: 'Anforderungen an die Lichtgestaltung', max: 6 },
            { label: 'Anforderungen an die Raumzuordnung und Raumproportion', max: 6 },
            { label: 'technische Ausrüstung', max: 6 },
            { label: 'Farb- und Materialgestaltung', max: 9 },
            { label: 'konstruktive Detailgestaltung', max: 9 },
        ],
        criteriaRef: null,
        bounds: [10, 18, 26, 34, 42],
        maxConversion: 50,
        conversionDefault: 20,
        conversionSource: '§ 36 Abs. 2',
        costRules: 'Anrechenbar sind die Kosten der Baukonstruktion (§ 33 Abs. 1). Kosten für Technische Anlagen, die der Auftragnehmer nicht fachlich plant oder deren Ausführung er nicht fachlich überwacht, sind bis zu einem Betrag von 25 Prozent der sonstigen anrechenbaren Kosten vollständig und mit dem darüber hinausgehenden Betrag zur Hälfte anrechenbar (§ 33 Abs. 2). Nicht anrechenbar sind insbesondere die Kosten für das Herrichten, die nichtöffentliche Erschließung, Ausstattung und Kunstwerke, soweit der Auftragnehmer sie weder plant noch bei der Beschaffung mitwirkt oder ihre Ausführung oder ihren Einbau fachlich überwacht (§ 33 Abs. 3).',
        maintenanceLp8: true,
    },
    freianlagen: {
        key: 'freianlagen',
        name: 'Freianlagen',
        part: 'Objektplanung',
        paragraphs: { services: '§ 39', fees: '§ 40', basics: '§ 38' },
        url: 'https://www.gesetze-im-internet.de/hoai_2013/__40.html',
        table: HOAI_TABLE_FREIANLAGEN,
        basis: 'costs',
        basisLabel: 'Anrechenbare Kosten in Euro',
        zones: 5,
        zoneLabels: ['sehr geringe Anforderungen', 'geringe Anforderungen', 'durchschnittliche Anforderungen', 'hohe Anforderungen', 'sehr hohe Anforderungen'],
        phases: [
            { name: 'Grundlagenermittlung', share: 3 },
            { name: 'Vorplanung', share: 10 },
            { name: 'Entwurfsplanung', share: 16 },
            { name: 'Genehmigungsplanung', share: 4 },
            { name: 'Ausführungsplanung', share: 25 },
            { name: 'Vorbereitung der Vergabe', share: 7 },
            { name: 'Mitwirkung bei der Vergabe', share: 3 },
            { name: 'Objektüberwachung – Bauüberwachung und Dokumentation', share: 30 },
            { name: 'Objektbetreuung', share: 2 },
        ],
        criteria: [
            { label: 'Anforderungen an die Einbindung in die Umgebung', max: 8 },
            { label: 'Anforderungen an Schutz, Pflege und Entwicklung von Natur und Landschaft', max: 8 },
            { label: 'Anzahl der Funktionsbereiche', max: 6 },
            { label: 'gestalterische Anforderungen', max: 8 },
            { label: 'Ver- und Entsorgungseinrichtungen', max: 6 },
        ],
        criteriaRef: null,
        bounds: [8, 15, 22, 29, 36],
        maxConversion: 33,
        conversionDefault: 20,
        conversionSource: '§ 40 Abs. 6 i. V. m. § 36 Abs. 1',
        costRules: 'Anrechenbar sind die Kosten für Außenanlagen, insbesondere für die in § 38 Abs. 1 Nr. 1 bis 8 genannten Bauwerke und Anlagen, soweit diese durch den Auftragnehmer geplant oder überwacht werden. Nicht anrechenbar sind die Kosten für das Gebäude sowie die in § 33 Absatz 3 genannten Kosten und für den Unter- und Oberbau von Fußgängerbereichen, ausgenommen die Kosten für die Oberflächenbefestigung (§ 38 Abs. 2).',
        maintenanceLp8: true,
    },
    ingenieurbauwerke: {
        key: 'ingenieurbauwerke',
        name: 'Ingenieurbauwerke',
        part: 'Objektplanung',
        paragraphs: { services: '§ 43', fees: '§ 44', basics: '§ 42' },
        url: 'https://www.gesetze-im-internet.de/hoai_2013/__44.html',
        table: HOAI_TABLE_INGENIEURBAUWERKE,
        basis: 'costs',
        basisLabel: 'Anrechenbare Kosten in Euro',
        zones: 5,
        zoneLabels: ['sehr geringe Anforderungen', 'geringe Anforderungen', 'durchschnittliche Anforderungen', 'hohe Anforderungen', 'sehr hohe Anforderungen'],
        phases: [
            { name: 'Grundlagenermittlung', share: 2 },
            { name: 'Vorplanung', share: 20 },
            { name: 'Entwurfsplanung', share: 25 },
            { name: 'Genehmigungsplanung', share: 5 },
            { name: 'Ausführungsplanung', share: 15 },
            { name: 'Vorbereitung der Vergabe', share: 13 },
            { name: 'Mitwirkung bei der Vergabe', share: 4 },
            { name: 'Bauoberleitung', share: 15 },
            { name: 'Objektbetreuung', share: 1 },
        ],
        criteria: [
            { label: 'geologische und baugrundtechnische Gegebenheiten', max: 5 },
            { label: 'technische Ausrüstung und Ausstattung', max: 5 },
            { label: 'Einbindung in die Umgebung oder in das Objektumfeld', max: 5 },
            { label: 'Umfang der Funktionsbereiche oder der konstruktiven oder technischen Anforderungen', max: 10 },
            { label: 'fachspezifische Bedingungen', max: 15 },
        ],
        criteriaRef: null,
        bounds: [10, 17, 25, 33, 40],
        maxConversion: 33,
        conversionDefault: 20,
        conversionSource: '§ 44 Abs. 6',
        costRules: 'Anrechenbar sind die Kosten der Baukonstruktion; die Kosten für Anlagen der Maschinentechnik, die der Zweckbestimmung des Ingenieurbauwerks dienen, soweit der Auftragnehmer diese plant oder deren Ausführung überwacht (§ 42 Abs. 1). Technische Anlagen, die der Auftragnehmer nicht fachlich plant oder überwacht, sind bis zum Betrag von 25 Prozent der sonstigen anrechenbaren Kosten vollständig und darüber zur Hälfte anrechenbar (§ 42 Abs. 2). Nicht anrechenbar sind, soweit der Auftragnehmer die Anlagen weder plant noch ihre Ausführung überwacht, u. a. die Kosten für das Herrichten des Grundstücks, die öffentliche und die nichtöffentliche Erschließung, Außenanlagen, verkehrsregelnde Maßnahmen während der Bauzeit sowie Ausstattung und Nebenanlagen (§ 42 Abs. 3).',
        maintenanceLp8: true,
    },
    verkehrsanlagen: {
        key: 'verkehrsanlagen',
        name: 'Verkehrsanlagen',
        part: 'Objektplanung',
        paragraphs: { services: '§ 47', fees: '§ 48', basics: '§ 46' },
        url: 'https://www.gesetze-im-internet.de/hoai_2013/__48.html',
        table: HOAI_TABLE_VERKEHRSANLAGEN,
        basis: 'costs',
        basisLabel: 'Anrechenbare Kosten in Euro',
        zones: 5,
        zoneLabels: ['sehr geringe Anforderungen', 'geringe Anforderungen', 'durchschnittliche Anforderungen', 'hohe Anforderungen', 'sehr hohe Anforderungen'],
        phases: [
            { name: 'Grundlagenermittlung', share: 2 },
            { name: 'Vorplanung', share: 20 },
            { name: 'Entwurfsplanung', share: 25 },
            { name: 'Genehmigungsplanung', share: 8 },
            { name: 'Ausführungsplanung', share: 15 },
            { name: 'Vorbereitung der Vergabe', share: 10 },
            { name: 'Mitwirkung bei der Vergabe', share: 4 },
            { name: 'Bauoberleitung', share: 15 },
            { name: 'Objektbetreuung', share: 1 },
        ],
        criteria: [
            { label: 'geologische und baugrundtechnische Gegebenheiten', max: 5 },
            { label: 'technische Ausrüstung und Ausstattung', max: 5 },
            { label: 'Einbindung in die Umgebung oder das Objektumfeld', max: 15 },
            { label: 'Umfang der Funktionsbereiche oder der konstruktiven oder technischen Anforderungen', max: 10 },
            { label: 'fachspezifische Bedingungen', max: 5 },
        ],
        criteriaRef: null,
        bounds: [10, 17, 25, 33, 40],
        maxConversion: 33,
        conversionDefault: 20,
        conversionSource: '§ 48 Abs. 6',
        costRules: 'Anrechenbar sind die Kosten der Baukonstruktion und, soweit der Auftragnehmer sie plant oder ihre Ausführung überwacht, die Kosten der Ausstattung einschließlich der Entwässerungsanlagen (§ 46 Abs. 1); Technische Anlagen, die er nicht fachlich plant oder überwacht, bis 25 Prozent der sonstigen anrechenbaren Kosten vollständig, darüber zur Hälfte (§ 46 Abs. 2). Für die Leistungsphasen 1 bis 7 und 9 sind Erdarbeiten einschließlich Felsarbeiten bis zu 40 Prozent der sonstigen anrechenbaren Kosten nach Abs. 1 und 10 Prozent der Kosten für Ingenieurbauwerke anrechenbar, wenn für diese nicht gleichzeitig Grundleistungen nach § 43 übertragen werden (§ 46 Abs. 4). Bei mehrstreifigen Straßen sind die Kosten zu 85, 70 oder 60 Prozent, bei zweigleisigen Gleis- und Bahnsteiganlagen zu 90 Prozent anrechenbar (§ 46 Abs. 5).',
        maintenanceLp8: true,
    },
    tragwerk: {
        key: 'tragwerk',
        name: 'Tragwerksplanung',
        part: 'Fachplanung',
        paragraphs: { services: '§ 51', fees: '§ 52', basics: '§ 50' },
        url: 'https://www.gesetze-im-internet.de/hoai_2013/__52.html',
        table: HOAI_TABLE_TRAGWERK,
        basis: 'costs',
        basisLabel: 'Anrechenbare Kosten in Euro',
        zones: 5,
        zoneLabels: ['sehr geringe Anforderungen', 'geringe Anforderungen', 'durchschnittliche Anforderungen', 'hohe Anforderungen', 'sehr hohe Anforderungen'],
        phases: [
            { name: 'Grundlagenermittlung', share: 3 },
            { name: 'Vorplanung', share: 10 },
            { name: 'Entwurfsplanung', share: 15 },
            { name: 'Genehmigungsplanung', share: 30 },
            { name: 'Ausführungsplanung', share: 40 },
            { name: 'Vorbereitung der Vergabe', share: 2 },
        ],
        criteria: [],
        criteriaRef: 'Anlage 14 Nummer 14.2',
        bounds: null,
        maxConversion: 50,
        conversionDefault: 20,
        conversionSource: '§ 52 Abs. 4',
        costRules: 'Bei Gebäuden und zugehörigen baulichen Anlagen sind 55 Prozent der Baukonstruktionskosten und 10 Prozent der Kosten der Technischen Anlagen anrechenbar (§ 50 Abs. 1); bei einem hohen Anteil an Kosten der Gründung und der Tragkonstruktionen kann in Textform die Ermittlung nach Abs. 3 vereinbart werden (§ 50 Abs. 2). Bei Ingenieurbauwerken sind 90 Prozent der Baukonstruktionskosten und 15 Prozent der Kosten der Technischen Anlagen anrechenbar (§ 50 Abs. 3). Für Traggerüste bei Ingenieurbauwerken sind die Herstellkosten einschließlich der zugehörigen Kosten für Baustelleneinrichtungen anrechenbar (§ 50 Abs. 4).',
        maintenanceLp8: false,
    },
    tga: {
        key: 'tga',
        name: 'Technische Ausrüstung',
        part: 'Fachplanung',
        paragraphs: { services: '§ 55', fees: '§ 56', basics: '§ 54' },
        url: 'https://www.gesetze-im-internet.de/hoai_2013/__56.html',
        table: HOAI_TABLE_TGA,
        basis: 'costs',
        basisLabel: 'Anrechenbare Kosten in Euro',
        zones: 3,
        zoneLabels: ['geringe Anforderungen', 'durchschnittliche Anforderungen', 'hohe Anforderungen'],
        phases: [
            { name: 'Grundlagenermittlung', share: 2 },
            { name: 'Vorplanung', share: 9 },
            { name: 'Entwurfsplanung', share: 17 },
            { name: 'Genehmigungsplanung', share: 2 },
            { name: 'Ausführungsplanung', share: 22 },
            { name: 'Vorbereitung der Vergabe', share: 7 },
            { name: 'Mitwirkung bei der Vergabe', share: 5 },
            { name: 'Objektüberwachung – Bauüberwachung', share: 35 },
            { name: 'Objektbetreuung', share: 1 },
        ],
        criteria: [
            { label: 'Anzahl der Funktionsbereiche', max: null },
            { label: 'Integrationsansprüche', max: null },
            { label: 'technische Ausgestaltung', max: null },
            { label: 'Anforderungen an die Technik', max: null },
            { label: 'konstruktive Anforderungen', max: null },
        ],
        criteriaRef: null,
        bounds: null,
        maxConversion: 50,
        conversionDefault: 20,
        conversionSource: '§ 56 Abs. 5',
        costRules: 'Das Honorar richtet sich für das jeweilige Objekt nach der Summe der anrechenbaren Kosten der Anlagen jeder Anlagengruppe; für nutzungsspezifische Anlagen nur, wenn die Anlagen funktional gleichartig sind (§ 54 Abs. 1). Nicht anrechenbar sind die Kosten für die nichtöffentliche Erschließung und die Technischen Anlagen in Außenanlagen, soweit der Auftragnehmer diese nicht plant oder ihre Ausführung nicht überwacht (§ 54 Abs. 4). Werden Teile der Technischen Ausrüstung in Baukonstruktionen ausgeführt, kann in Textform vereinbart werden, dass die Kosten hierfür ganz oder teilweise anrechenbar sind (§ 54 Abs. 5).',
        maintenanceLp8: true,
    },
};
//# sourceMappingURL=hoai-services.js.map